/**
 * AI Presets Component - One-tap filter bundles
 * Displays preset cards that users can click to apply predefined filters
 */
import React from 'react';
import { Badge } from '../ui/badge';
import {
  Sparkles,
  Users,
  Wallet,
  Building2,
  GraduationCap,
  Leaf,
  Gauge,
  Briefcase,
  Package,
  Truck,
  School,
  Map,
  Route,
  Bike,
  BatteryFull,
  HardHat,
  Shovel,
  Timer,
  Dumbbell,
  CheckCircle2,
  Heart,
  Feather,
  Fish,
  Sailboat,
  Waves,
  Car,
  Fuel,
} from 'lucide-react';
import { getPresetsForVehicle } from '../../config/vehicles/aiPresets';

// Map lucide-react icon name strings from the preset config to icon components
const ICON_MAP = {
  Users, Wallet, Building2, GraduationCap, Leaf, Gauge, Briefcase, Package,
  Truck, School, Map, Route, Bike, BatteryFull, HardHat, Shovel, Timer,
  Dumbbell, CheckCircle2, Heart, Feather, Fish, Sailboat, Waves, Car, Fuel,
};

const PresetIcon = ({ name, className }) => {
  const Icon = ICON_MAP[name] || Sparkles;
  return <Icon className={className} />;
};

const AIPresets = ({
  vehicleType,
  country = 'SL',
  onApplyPreset,
  activePresetId = null,
  className = '',
  showHeader = true,
}) => {
  const presets = getPresetsForVehicle(vehicleType, country);

  if (presets.length === 0) return null;

  return (
    <div className={className} data-testid="ai-presets">
      {showHeader && (
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <span className="text-sm font-semibold text-gray-700">Quick Filters</span>
          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
            AI Powered
          </Badge>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => {
          const isActive = activePresetId === preset.id;
          return (
            <button
              key={preset.id}
              data-testid={`ai-preset-${preset.id}`}
              onClick={() => onApplyPreset(preset)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
                isActive
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50'
              }`}
            >
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${
                  isActive ? 'bg-purple-200 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <PresetIcon name={preset.icon} className="h-4 w-4" />
              </span>
              <div className="text-left">
                <div className="text-sm font-medium">{preset.name}</div>
                <div className="text-xs text-gray-500 hidden sm:block">{preset.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AIPresets;
